import { Controller } from "stimulus"

const CAROUSEL_IDENTIFIER = "block--carousel-component"

// Owns reveal and dismissal for the sidebar announcement carousel.
//
// Dismissals live in localStorage (per browser), which is how the hardcoded card this replaced
// worked - keeping that means no new table, endpoint, or write path on a read-heavy page. The
// key prefix (`alert_bar_<key>_dismissed`) is carried over from alert_bar_controller.js.
//
// The root is server-rendered `hidden` and only revealed once the dismissed slides have been
// pruned and the carousel re-snapshotted, so there's no flash of a card the user already closed.
export default class SidebarAnnouncementsComponentController extends Controller {
  connect() {
    this.filterDismissed()
    // Stimulus makes no promise about parent-before-child connect order, and Turbo cache restores
    // perturb it - deferring a frame means refresh() lands after the carousel has connected under
    // either order. Nothing is visible meanwhile: the root is still hidden.
    requestAnimationFrame(() => this.syncAndReveal())
  }

  dismiss(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    const target = event.currentTarget || event.target
    const card = target.closest("[data-announcement-key]")
    if (!card) return

    window.localStorage.setItem(this.storageKey(card.dataset.announcementKey), "true")
    this.removeSlide(card)
    this.syncAndReveal()
  }

  filterDismissed() {
    this.cards.forEach((card) => {
      if (window.localStorage.getItem(this.storageKey(card.dataset.announcementKey)) === "true") {
        this.removeSlide(card)
      }
    })
  }

  // Cards are wrapped in a slide div by the carousel - drop the wrapper too, or the carousel
  // counts an empty slide.
  removeSlide(card) {
    const container = card.closest(`[data-${CAROUSEL_IDENTIFIER}-target="container"]`)
    const slide = container ? Array.from(container.children).find((child) => child.contains(card)) : null
    ;(slide || card).remove()
  }

  syncAndReveal() {
    const carousel = this.carouselController
    if (carousel) carousel.refresh()

    if (this.cards.length === 0) {
      this.element.remove()
      return
    }

    this.element.classList.remove("hidden")
  }

  get cards() {
    return Array.from(this.element.querySelectorAll("[data-announcement-key]"))
  }

  get carouselController() {
    const element = this.element.querySelector(`[data-controller~="${CAROUSEL_IDENTIFIER}"]`)
    if (!element) return null

    return this.application.getControllerForElementAndIdentifier(element, CAROUSEL_IDENTIFIER)
  }

  storageKey(key) {
    return `alert_bar_${key}_dismissed`
  }
}
